import { useEffect, useState } from "react";
import { type AxiosError } from "axios";
import { DateTime } from "luxon";
import type { ExtractRouteParams } from "react-router";
import { useHistory, useParams } from "react-router-dom";
import { AUTH_URLS, createTimesheetUrl } from "src/pages/authenticated/Routes";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import {
  Asterisk,
  Breakpoints,
  Button,
  Card,
  Checkbox,
  DateInput,
  fontMedium,
  fontSmall,
  fontXSmall,
  formatDate,
  formatTime,
} from "swing-components";

import { AlertError, ContentSingleColumn, useModal } from "~components";
import { translate } from "~utils";

import { NonCompliantMealBreakActionSheet } from "../components/NonCompliantMealBreakActionSheet";
import { PaycodeReasons } from "../components/PaycodeReasons";
import { TimecardEntryForm } from "../components/TimecardEntryForm";
import {
  getWeekRangeFromDateTime,
  type PayCode,
  type TimeCard,
  type TimesheetEntry,
} from "../utils";

/***** Types *****/
// Represents the URL parameters for this page.
type URLParams = ExtractRouteParams<typeof AUTH_URLS.timesheetEntryEdit, string>;

/**
 * TODO: Potentially can delete this file since it's not used anymore
 */
export function TimesheetEntry() {
  // const { indexId } = useParams<URLParams>();

  // Fetch timesheet data if we have an entryIndex
  // const {
  //   data: timesheetData,
  //   error,
  //   isLoading,
  // } = useQuery({
  //   queryKey: ["fetchTimesheet", indexId],
  //   queryFn: () =>
  //     GET("/api/sub/timesheet/{interval-id}", {
  //       pathParams: { "interval-id": indexId },
  //     }),
  //   enabled: !!indexId,
  // });

  // const timesheet = timesheetData?.data || [];

  // Find the specific entry by index if we have timesheet data and entryIndex
  // const entry = indexId ? timesheet.find((_, index) => index.toString() === indexId) : undefined;

  // Create an error if entryIndex is provided but no matching entry is found
  // const entryNotFoundError =
  //   indexId && !entry && !isLoading
  //     ? (new Error(`Timesheet entry with index ${indexId} not found`) as AxiosError)
  //     : null;

  // Combine API error with entry not found error
  // const combinedError = error || entryNotFoundError;

  // return <TimesheetEntryView entry={entry} error={combinedError} isLoading={isLoading} />;
  return null;
}

type TimesheetEntryViewProps = {
  entry?: TimesheetEntry;
  onSubmit: (formData: TimesheetEntry) => void;
  error?: AxiosError | null;
  isLoading?: boolean;
};

export function TimesheetEntryView(props: TimesheetEntryViewProps) {
  const { entry, isLoading, error, onSubmit } = props;

  /********* State ********/
  const [formData, setFormData] = useState<TimesheetEntry>(() => {
    // Initialize with entry data if available, otherwise create a new Paycode entry
    if (entry) {
      return entry;
    } else {
      return {
        type: "PAYCODE",
        id: "",
        durationInMinutes: 0,
        date: DateTime.now(),
      };
    }
  });
  const [isCertified, setIsCertified] = useState(false);
  const [missingBreakReason, setMissingBreakReason] = useState<string | undefined>();

  // Feature flag for missing break action sheet - hardcoded for now
  // TODO: Remove this once we align on how to manage the selected reason in UKG
  const isMissingBreakActionSheetEnabled = false;

  // Modal for missing break action sheet
  const { openModal, closeModal } = useModal({
    component: (
      <NonCompliantMealBreakActionSheet
        onDismiss={() => closeModal()}
        onSubmit={() => {
          console.log("Submitted with missing break reason:", missingBreakReason);
          // TODO: Submit to API when ready
          closeModal();
        }}
        isSubmissionPending={false}
        onOptionChange={() => {}}
      />
    ),
    modalOptions: {
      cssClass: "action-sheet-modal",
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    },
  });

  // Update formData when entry changes
  useEffect(() => {
    if (entry) {
      // Update with the existing entry data
      setFormData(entry);
    } else {
      // Update for a new Paycode entry with current date
      setFormData({
        type: "PAYCODE",
        id: "",
        durationInMinutes: 0,
        date: DateTime.now(),
      });
    }
    // Reset certification when entry changes
    setIsCertified(false);
    // Reset missing break reason when entry changes
    setMissingBreakReason(undefined);
  }, [entry]);

  /********* Helpers ********/
  const { weekStart, weekEnd } = getWeekRangeFromDateTime(DateTime.now().toISODate());
  const nextDay = weekEnd.plus({ days: 1 }).toISODate();
  const previousDay = weekStart.minus({ days: 1 }).toISODate();

  // Extract date from entry
  // function getEntryDate(entry: TimesheetEntry | undefined) {
  //   if (!entry) {
  //     return DateTime.now().toISODate();
  //   }

  //   if (entry.type === "PAYCODE") {
  //     return entry.date;
  //   } else {
  //     if (entry.date) {
  //       return entry.date;
  //     }

  //     const punchInOrOut = entry.entries.find(
  //       (entry) => entry.type === "PUNCH_IN" || entry.type === "PUNCH_OUT",
  //     );
  //     return punchInOrOut ? punchInOrOut.ts : undefined;
  //   }
  // }

  function handleDateChange(date: string) {
    setFormData((prev) => {
      if (!prev) return prev;
      return { ...prev, date: DateTime.fromISO(date) };
    });
  }

  function handleFormDataChange(update: Partial<TimesheetEntry>) {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return prev;

      if (prev.type === "TIMECARD" && "entries" in update) {
        // Handle timecard updates
        return { ...prev, ...update } as TimeCard;
      } else if (prev.type === "PAYCODE" && "durationInMinutes" in update) {
        // Handle paycode updates
        return { ...prev, ...update } as PayCode;
      }

      return prev;
    });
  }

  // handle paycode reason changes
  function handlePaycodeReasonChange(reason: PayCode["payCode"]) {
    setFormData((prev) => {
      if (!prev || prev.type !== "PAYCODE") return prev;
      return { ...prev, payCode: reason } as PayCode;
    });
  }

  // Helper function to handle form submission
  function handleSubmit() {
    if (!formData) return;

    // For timecards, check if we need to show the missing break action sheet
    if (formData.type === "TIMECARD" && isMissingBreakActionSheetEnabled) {
      openModal();
      return;
    }

    console.log("Submitting form data:", formData);

    onSubmit?.(formData);
  }

  // Check if form is valid
  const isFormValid = Boolean(
    formData &&
      isCertified &&
      formData.date &&
      (formData.type === "TIMECARD"
        ? formData.entries.some((entry) => entry.type === "PUNCH_IN") &&
          formData.entries.some((entry) => entry.type === "PUNCH_OUT")
        : formData.type === "PAYCODE" &&
          formData.payCode &&
          formData.durationInMinutes !== undefined &&
          formData.durationInMinutes > 0),
  );

  return (
    <ScorePage isLoading={isLoading} hasBack title={entry ? "Edit Entry" : "Add Entry"}>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: 16,
            [Breakpoints.mediumDeviceAndUp]: { padding: 0 },
          }}
        >
          {/* Error */}
          {error && <AlertError />}

          {/* Display the entry if it exists */}
          {entry && (
            <>
              {/* If it's timcard, show the request details */}
              {entry.type === "TIMECARD" && (
                <RequestDetails
                  schoolName={entry.school.name}
                  date={entry.date}
                  startTime={entry.request.startTime}
                  endTime={entry.request.endTime}
                />
              )}
              {/* If it's paycode, show the reasons */}
              {entry.type === "PAYCODE" && (
                <PaycodeReasons
                  reason={formData?.type === "PAYCODE" ? formData.payCode : undefined}
                  onReasonChange={handlePaycodeReasonChange}
                />
              )}

              {/* Date */}
              {/* TODO: Double check disabled date range logic */}
              {/* <Date
                value={getEntryDate(entry)}
                onChange={handleDateChange}
                disabledDateRanges={[
                  { start: "1900-01-01", end: previousDay },
                  { start: nextDay, end: "2100-12-31" },
                ]}
              /> */}

              {/* Time Input Form */}
              {/* The props and stuff is based on the entry type */}
              {entry.type === "TIMECARD" && (
                <TimecardEntryForm
                  entryType="TIMECARD"
                  timecardPunches={entry.entries}
                  onChange={handleFormDataChange}
                />
              )}
              {entry.type === "PAYCODE" && (
                <TimecardEntryForm
                  entryType="PAYCODE"
                  paycodeDurationInMinutes={entry.durationInMinutes}
                  onChange={handleFormDataChange}
                />
              )}

              {/* Certification Checkbox */}
              <CertificationCheckbox
                isChecked={isCertified}
                onChange={(isChecked) => setIsCertified(isChecked)}
              />

              {/* Button Group */}
              <ButtonGroup
                isFormValid={isFormValid}
                formData={formData}
                onSubmit={handleSubmit}
                entryType={entry.type}
              />
            </>
          )}

          {/* Handle case when there's no entry */}
          {/* This implies a new Paycode entry is being created for the sub */}
          {!entry && (
            <>
              {/* Reasons */}
              <PaycodeReasons
                reason={formData?.type === "PAYCODE" ? formData.payCode : undefined}
                onReasonChange={handlePaycodeReasonChange}
              />

              {/* Date */}
              <Card>
                <div
                  css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                    {translate("LABEL_DATE")}
                    <Asterisk />
                  </p>
                  {/* Can only select within the current interval range */}
                  <DateInput
                    value={formData?.date?.toISO()}
                    onChange={handleDateChange}
                    disabledDateRanges={[
                      { start: "1900-01-01", end: previousDay },
                      { start: nextDay, end: "2100-12-31" },
                    ]}
                  />
                </div>
              </Card>

              {/* Time Input Form */}
              <TimecardEntryForm entryType="PAYCODE" onChange={handleFormDataChange} />

              {/* Certification Checkbox */}
              <CertificationCheckbox
                isChecked={isCertified}
                onChange={(isChecked) => setIsCertified(isChecked)}
              />

              {/* Button Group */}
              <ButtonGroup
                isFormValid={isFormValid}
                formData={formData}
                onSubmit={handleSubmit}
                entryType="PAYCODE"
              />
            </>
          )}
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

export function RequestDetails(props: {
  schoolName: string;
  date: DateTime;
  startTime: DateTime;
  endTime: DateTime;
}) {
  const { schoolName, date, startTime, endTime } = props;

  const shouldRender = schoolName || date || startTime || endTime;

  if (!shouldRender) {
    return null;
  }

  return (
    <Card>
      {schoolName && <p css={{ margin: 0 }}>{schoolName}</p>}

      {date && (
        <p css={{ margin: 0, ...fontSmall({ fontWeight: 600 }), lineHeight: "24px" }}>
          {formatDate({ date, format: "Wed, Jan 1" })}
        </p>
      )}

      {(startTime || endTime) && (
        <p css={{ margin: 0, ...fontXSmall() }}>
          {startTime && formatTime({ date: startTime, format: "1:00 pm" })}
          {startTime && endTime && " - "}
          {endTime && formatTime({ date: endTime, format: "1:00 pm" })}
        </p>
      )}
    </Card>
  );
}

// type DateProps = {
//   disabledDateRanges?: Array<{ start: string; end: string }>;
//   value?: DateTime | undefined;
//   onChange: (date: string) => void;
//   isDisabled?: boolean;
// };

// function Date(props: DateProps) {
//   const { disabledDateRanges, value, onChange, isDisabled } = props;

//   return (
//     <Card>
//       <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//         <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
//           {translate("LABEL_DATE")}
//           <Asterisk />
//         </p>

//         {/* Can only select within the current interval range */}
//         <DateInput
//           value={value?.toISO()}
//           onChange={onChange}
//           disabledDateRanges={disabledDateRanges}
//           isDisabled={isDisabled}
//         />
//       </div>
//     </Card>
//   );
// }

function CertificationCheckbox(props: {
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
}) {
  const { isChecked, onChange } = props;

  return (
    <Card>
      {/* Offset Card padding since Checkbox has its own */}
      <div css={{ marginLeft: -16, marginRight: -16 }}>
        <Checkbox
          label={translate("TIMECARD_ENTRY_CERTIFICATION_CHECKBOX_LABEL")}
          isChecked={isChecked}
          onCheckedChange={onChange}
        />
      </div>
    </Card>
  );
}

type ButtonGroupProps = {
  isFormValid: boolean;
  formData: TimesheetEntry;
  onSubmit: (formData: TimesheetEntry) => void;
  entryType: TimesheetEntry["type"];
};

function ButtonGroup(props: ButtonGroupProps) {
  /*** Props ***/
  const { isFormValid, formData, entryType, onSubmit } = props;

  /*** Hooks ***/
  const history = useHistory();
  const { weekId } = useParams<URLParams>();

  /*** Helpers ***/
  const hasCancelButton = entryType === "PAYCODE";

  /*** Render ***/
  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: 16,
        ...(hasCancelButton && {
          [Breakpoints.mediumDeviceAndUp]: {
            flexDirection: "row-reverse", // primary on right, cancel on left
            justifyContent: "space-between",
            alignItems: "flex-start",
          },
        }),
      }}
    >
      {/* Submit */}
      <Button
        type="primary"
        onClick={() => {
          if (!isFormValid) return;
          onSubmit?.(formData);
        }}
        isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
        status={!isFormValid ? "disabled" : "enabled"}
      >
        {translate("TIMESHEET_SUBMIT_ENTRY_LABEL")}
      </Button>
      {/* Cancel - only show for Paycode type */}
      {hasCancelButton && (
        <Button
          type="secondary"
          isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
          // Go back to the timesheet list
          onClick={() => history.push(createTimesheetUrl(weekId))}
        >
          {translate("LABEL_CANCEL")}
        </Button>
      )}
    </div>
  );
}
